import { Warning, User, Truck, Images, Package, CheckCircle } from "@phosphor-icons/react";
import { MaterialIcon } from "../../lib/materialIcons";
import { Checkbox } from "../ui/checkbox";

const fmt = (n) => (Number.isInteger(n) ? n : Number(n).toFixed(2).replace(/\.00$/, ""));

const Card = ({ icon: Icon, title, children, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-3"><Icon size={14} /> {title}</div>
    {children}
  </div>
);

const Row = ({ k, v, mono }) => (
  <div className="flex justify-between gap-3 text-sm py-1"><span className="text-slate-500">{k}</span><span className={`font-semibold text-right ${mono ? "font-mono" : ""}`}>{v || "—"}</span></div>
);

export function VerifyStep({ x, selected, serials, form, photos, pending, shortLines, ack, setAck }) {
  const remaining = pending.map((l) => ({ ...l, rem: l.pending - (selected.find((s) => s.key === l.key)?.qty || 0) })).filter((l) => l.rem > 0);
  return (
    <div className="space-y-4" data-testid="verify-step">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card icon={User} title="Customer" testid="verify-customer">
          <Row k="Name" v={x.customer_name || x.text} />
          <Row k="Mobile" v={x.customer_mobile} mono />
          <Row k="Address" v={x.customer_address} />
        </Card>
        <Card icon={Truck} title="Vehicle & Time" testid="verify-transport">
          <Row k="Vehicle" v={form.vehicle_no.toUpperCase()} mono />
          <Row k="Driver" v={form.driver_name} />
          <Row k="Date · Time" v={`${form.dispatch_date.split("-").reverse().join("/")} · ${form.dispatch_time}`} mono />
          <Row k="Photos" v={`${photos.length} attached`} />
        </Card>
      </div>

      <Card icon={Package} title={`Materials (${selected.length})`} testid="verify-list">
        <div className="divide-y divide-slate-100">
          {selected.map((s) => {
            const sr = (serials[s.key] || []).filter((v) => v.trim());
            const short = s.serial && sr.length < Math.floor(s.qty);
            return (
              <div key={s.key} className="py-2.5 flex gap-3">
                <MaterialIcon iconKey={s.icon_key} size={36} />
                <div className="min-w-0 flex-1">
                  <div className="flex justify-between gap-2"><span className="font-semibold text-sm">{s.label}</span><span className="font-mono font-bold text-sm">{fmt(s.qty)} {s.unit}</span></div>
                  {s.serial && (
                    <div className={`mt-1 text-[11px] font-mono ${short ? "text-amber-700" : "text-emerald-700"}`}>
                      {short ? <Warning size={11} className="inline mr-1" weight="fill" /> : <CheckCircle size={11} className="inline mr-1" weight="fill" />}
                      Serials {sr.length}/{Math.floor(s.qty)}{sr.length > 0 && `: ${sr.join(", ")}`}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {photos.length > 0 && (
        <Card icon={Images} title="Photos" testid="verify-photos">
          <div className="grid grid-cols-6 sm:grid-cols-10 gap-1.5">{photos.map((p) => <img key={p.id} src={p.url} alt="" className="aspect-square w-full rounded object-cover ring-1 ring-slate-200" />)}</div>
        </Card>
      )}

      {remaining.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-slate-100 px-4 py-3 text-xs" data-testid="verify-pending">
          <div className="font-bold text-slate-700 mb-1">Still pending after this dispatch</div>
          {remaining.map((l) => <div key={l.key} className="flex justify-between"><span>{l.label}</span><span className="font-mono">{fmt(l.rem)} {l.unit}</span></div>)}
        </div>
      )}

      {shortLines.length > 0 && (
        <label className="flex gap-3 items-start rounded-xl border-2 border-amber-400 bg-amber-50 px-4 py-3 cursor-pointer" data-testid="serial-short-ack">
          <Checkbox data-testid="serial-short-ack-check" checked={ack} onCheckedChange={(v) => setAck(!!v)} className="mt-0.5" />
          <span className="text-xs text-amber-900"><b className="flex items-center gap-1 mb-0.5"><Warning size={14} weight="fill" /> Fewer serials than quantity</b>
            {shortLines.map((l) => l.label).join(", ")} — tick to dispatch anyway</span>
        </label>
      )}
    </div>
  );
}
